import { OrderChannel, OrderPaymentSchedule, OrderPpaDocTypes } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import { PaymentStatus } from '@afterpaytouch/portal-api/payments/types'
import { isBankAccount, isCard, isCashAppPay, Money, PaymentType } from '@afterpay/types'
import { isValidMoney, sumMoney } from '../../component/Money/utils'
import { OrderFeeType, PayNowScheduleOption, PaymentMethods } from './types'
import { PaymentArrangementSchedule } from '@afterpaytouch/portal-api/consumer/hardship/types'
import { isPaymentArrangementSchedule } from '../hardship/utils'
import { itemDueIn } from '../paymentSchedule/utils'
import { ppaDocumentLabel } from './constants'

export enum TotalOwingFeeDisplayType {
  Hidden = 'hidden',
  LateFee = 'late_fee',
  FailedPaymentFee = 'failed_payment_fee',
  FailedPaymentAndLateFee = 'failed_payment_and_late_fee',
}

interface CalculateAmountPayableProps {
  options: Array<{
    schedule?: OrderPaymentSchedule | PaymentArrangementSchedule
  }>
  payoutAmount?: Money
  makeAPaymentV2ModalEnabled?: boolean
}

export const isOrderTypeUnsupportedForPaymentMethod = (paymentMethod: PaymentMethods, paymentType: PaymentType): boolean => {
  const supportedOrderTypes = isCard(paymentMethod) || isBankAccount(paymentMethod) || isCashAppPay(paymentMethod) ? paymentMethod.supportedOrderTypes : null

  if (isCashAppPay(paymentMethod) && paymentType === PaymentType.PBI) {
    return false
  }
  if (supportedOrderTypes === null || !Array.isArray(supportedOrderTypes)) {
    return true
  }

  return !supportedOrderTypes.includes(paymentType)
}

export const isScheduleDueToday = (schedule: OrderPaymentSchedule): boolean =>
  schedule?.installmentDueDate != null && new Date(schedule.installmentDueDate).toDateString() === new Date().toDateString()

export const isScheduleOverdue = (schedule: OrderPaymentSchedule): boolean => schedule?.status === PaymentStatus.OVERDUE

export const isScheduleCompleted = (schedule: OrderPaymentSchedule): boolean => parseFloat(schedule?.amountPayable?.amount) <= 0

export const isSchedulePartialRefund = (schedule: OrderPaymentSchedule): boolean =>
  schedule?.status === PaymentStatus.PARTIAL_REFUND || schedule?.paymentStatus === PaymentStatus.PARTIAL_REFUND

export const isScheduleRefunded = (schedule: OrderPaymentSchedule): boolean =>
  schedule?.status === PaymentStatus.REFUNDED || schedule?.paymentStatus === PaymentStatus.REFUNDED

export const isScheduleOwed = (schedule: OrderPaymentSchedule): boolean => schedule?.status === PaymentStatus.OWED

export const isSchedulePayable = (schedule: OrderPaymentSchedule): boolean =>
  (isScheduleDueToday(schedule) && !isScheduleCompleted(schedule)) || isScheduleOverdue(schedule) || isScheduleOwed(schedule)

export const isScheduleDue = (schedule: OrderPaymentSchedule): boolean => isScheduleDueToday(schedule) || isScheduleOverdue(schedule)

export const isScheduleDueInFuture = (schedule: OrderPaymentSchedule): boolean => !isScheduleDueToday(schedule) && isScheduleOwed(schedule)

export const isFullyRefunded = (installment: OrderPaymentSchedule): boolean => {
  if (installment?.paymentStatus !== PaymentStatus.PAID) {
    return false
  }

  if (installment?.paymentRefundResponses?.length > 0) {
    const totalPaid = Number(sumMoney(...installment.paymentResponses.map((item) => item?.amount))?.amount)
    const totalRefunded = Number(sumMoney(...installment.paymentRefundResponses.map((item) => item?.amount))?.amount)
    return totalPaid === totalRefunded + parseFloat(installment?.orderFee?.amount)
  } else {
    return false
  }
}

export const isSchedulePaid = (schedule: OrderPaymentSchedule): boolean => schedule?.status === PaymentStatus.PAID && !isFullyRefunded(schedule)

export const hasScheduleFee = (schedule: OrderPaymentSchedule): boolean => isValidMoney(schedule?.owedFee)

export const isOrderPaymentSchedule = (schedule?: any): schedule is OrderPaymentSchedule => {
  return (schedule as OrderPaymentSchedule)?.originalInstallmentDueDate !== undefined
}

export const calculateAmountPayable = ({ options, payoutAmount }: CalculateAmountPayableProps): Money => {
  if (options && isPaymentArrangementSchedule(options[0]?.schedule)) {
    // @ts-ignore: OPERATION BLEED STOPPER
    return options?.reduce(
      // @ts-ignore: OPERATION BLEED STOPPER
      (prev, current) => {
        const schedule = current.schedule as PaymentArrangementSchedule
        const currentAmount = schedule?.outstandingBalance
        return {
          amount: parseFloat(String(Number(prev.amount) + Number(currentAmount.amount))).toFixed(2),
          currency: currentAmount.currency,
          symbol: currentAmount.symbol,
        }
      },
      { amount: '0', currency: undefined, symbol: '' }
    )
  }
  // @ts-ignore: OPERATION BLEED STOPPER
  if (payoutAmount != null && isValidMoney(payoutAmount)) {
    // @ts-ignore: OPERATION BLEED STOPPER
    return payoutAmount
  }
  if (options?.length === 1) {
    const schedule = options[0]?.schedule as OrderPaymentSchedule
    return schedule?.amountPayable
  }

  // @ts-ignore: OPERATION BLEED STOPPER
  return options?.reduce(
    // @ts-ignore: OPERATION BLEED STOPPER
    (prev, current) => {
      const schedule = current.schedule as OrderPaymentSchedule
      const currentAmount = schedule?.amountPayable
      return {
        amount: parseFloat(String(Number(prev.amount) + Number(currentAmount.amount))).toFixed(2),
        currency: currentAmount.currency,
        symbol: currentAmount.symbol,
      }
    },
    { amount: '0', currency: undefined, symbol: '' }
  )
}

export const validatePaymentSelection = (options: PayNowScheduleOption[] = []): boolean => {
  const reversedOptions = [...options].reverse()
  const lastSelectedIndex = reversedOptions.findIndex((option) => option.selected)
  const lastSelectedOption = reversedOptions[lastSelectedIndex]

  // @ts-ignore: OPERATION BLEED STOPPER
  if (options.length === 1 && isValidMoney(options[0].payoutAmount)) {
    return true
  }

  if (lastSelectedOption === undefined) {
    return false
  }

  return isScheduleDueInFuture(lastSelectedOption?.schedule as OrderPaymentSchedule)
    ? Boolean(
        reversedOptions
          .slice()
          .splice(lastSelectedIndex)
          .every(({ selected, schedule }) => selected && isSchedulePayable(schedule as OrderPaymentSchedule))
      )
    : true
}

export const getPaymentScheduleSetPayable = (schedules: OrderPaymentSchedule[]): OrderPaymentSchedule[] =>
  schedules?.filter((schedule) => isScheduleOverdue(schedule) || isScheduleOwed(schedule))

export const getFirstDueSchedule = (schedules: OrderPaymentSchedule[]): OrderPaymentSchedule =>
  getLastScheduleDueToday(schedules) ?? getLastScheduleOverdue(schedules) ?? getFirstScheduleOwed(schedules)

export const getLastScheduleDueToday = (schedules: OrderPaymentSchedule[] = []): OrderPaymentSchedule =>
  // @ts-ignore: OPERATION BLEED STOPPER
  [...schedules].reverse().find((schedule) => isScheduleDueToday(schedule) && isScheduleOwed(schedule))

export const getLastScheduleOverdue = (schedules: OrderPaymentSchedule[] = []): OrderPaymentSchedule =>
  // @ts-ignore: OPERATION BLEED STOPPER
  [...schedules].reverse().find((schedule) => isScheduleOverdue(schedule))
// @ts-ignore: OPERATION BLEED STOPPER
export const getFirstScheduleOwed = (schedules: OrderPaymentSchedule[] = []): OrderPaymentSchedule => schedules.find((schedule) => isScheduleOwed(schedule))

export const getOrderChannelLabel = (channel: OrderChannel): string => (channel?.includes('IN_STORE') ? 'inStore' : 'online')

export const hasInstallmentLastAndOwed = (schedules: OrderPaymentSchedule[] = []): boolean =>
  schedules.some((schedule) => Boolean(schedule?.isInstallmentLastAndOwed))

export const isFinalOrderDue = (schedules: OrderPaymentSchedule[] = []): boolean => {
  const firstDueSchedule = getFirstDueSchedule(schedules)
  return schedules[schedules?.length - 1] === firstDueSchedule
}

export const getPpaDocumentTitle = (docTypes: OrderPpaDocTypes): string => {
  return ppaDocumentLabel[docTypes] ?? 'fallback'
}

export const getTotalInstallmentCount = (installmentsCount: number, paymentUpFront: boolean | undefined, paymentType: PaymentType | undefined): number =>
  Boolean(paymentUpFront) && paymentType === PaymentType.PCL ? installmentsCount - 1 : installmentsCount

export const getInstallmentSequence = (installmentSequence: number, paymentUpFront: boolean | undefined, paymentType: PaymentType | undefined): number =>
  Boolean(paymentUpFront) && paymentType === PaymentType.PCL ? installmentSequence - 1 : installmentSequence

export const getDaysAfterInstallmentDueDate = (installmentDueDate: string | null | undefined, fromDate = new Date()): number => {
  if (installmentDueDate == null || installmentDueDate === '') {
    return 0
  }
  const dueIn = itemDueIn({ installmentDueDate }, fromDate)
  return dueIn < 0 ? -dueIn : 0
}

export const getFeeDisplayTypeFromFeeTypes = (
  orderFees: Array<{ amount: Money; feeType?: string | null }> | undefined,
  hasValidFee: boolean
): TotalOwingFeeDisplayType => {
  if (!hasValidFee) {
    return TotalOwingFeeDisplayType.Hidden
  }

  const validFees = (orderFees ?? []).filter((fee) => isValidMoney(fee?.amount))

  if (validFees.length === 0) {
    return TotalOwingFeeDisplayType.LateFee
  }

  const hasFailedPaymentFee = validFees.some((fee) => fee.feeType === OrderFeeType.FAILED_PAYMENT)
  const hasLateFee = validFees.some((fee) => fee.feeType == null || fee.feeType === OrderFeeType.FIRST || fee.feeType === OrderFeeType.SECOND)

  if (hasFailedPaymentFee && hasLateFee) {
    return TotalOwingFeeDisplayType.FailedPaymentAndLateFee
  }
  if (hasFailedPaymentFee) {
    return TotalOwingFeeDisplayType.FailedPaymentFee
  }
  return TotalOwingFeeDisplayType.LateFee
}

export interface SplitFeeAmounts {
  failedPaymentFee: Money | null
  lateFee: Money | null
}

export const getSplitFeeAmounts = (orderFees: Array<{ amount: Money; feeType?: string | null }> | undefined): SplitFeeAmounts => {
  const validFees = (orderFees ?? []).filter((fee) => isValidMoney(fee?.amount))

  if (validFees.length === 0) {
    return { failedPaymentFee: null, lateFee: null }
  }

  const failedPaymentFees = validFees.filter((fee) => fee.feeType === OrderFeeType.FAILED_PAYMENT)
  const lateFees = validFees.filter((fee) => fee.feeType == null || fee.feeType === OrderFeeType.FIRST || fee.feeType === OrderFeeType.SECOND)

  const failedPaymentFee =
    failedPaymentFees.length > 0 ? (failedPaymentFees.length === 1 ? failedPaymentFees[0].amount : sumMoney(...failedPaymentFees.map((f) => f.amount))) : null
  const lateFee = lateFees.length > 0 ? (lateFees.length === 1 ? lateFees[0].amount : sumMoney(...lateFees.map((f) => f.amount))) : null

  return { failedPaymentFee, lateFee }
}

export const getInstallmentOwingFeeDisplayType = (
  owedFee: Money | undefined,
  orderFees: Array<{ amount: Money; feeType?: string | null }> | undefined
): TotalOwingFeeDisplayType => {
  const hasValidFee = owedFee != null && isValidMoney(owedFee)
  return getFeeDisplayTypeFromFeeTypes(orderFees, hasValidFee)
}

export const getTotalOwingFeeDisplayType = (
  paymentScheduleSet: OrderPaymentSchedule[] | undefined,
  totalOwedFee: Money | undefined
): TotalOwingFeeDisplayType => {
  const hasValidFee = totalOwedFee != null && isValidMoney(totalOwedFee)
  const allOrderFees = (paymentScheduleSet ?? []).flatMap((schedule) => (schedule.orderFees ?? []) as Array<{ amount: Money; feeType?: string | null }>)
  return getFeeDisplayTypeFromFeeTypes(allOrderFees, hasValidFee)
}
